import { DateTime } from "luxon";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { SyntheticEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { EventTemplate } from "~/app/community-management/[communitySlug]/event-templates/_components/EventTemplatesList";
import { RouterInputs, api } from "~/utils/api";
import { hasEndedUX } from "~/utils/event";
import { urlSafeSlugify } from "~/utils/string";
import { correctDateToClientTz, mergeToTime } from "~/utils/time";
import { MissingUserData, getMissingProfileData } from "~/utils/user-helpers";
import { PanelStateTypes } from "~/state/eventDetailsRenderStore";
import { getTemplateForChapterLead } from "~/app/community-management/[communitySlug]/event-templates/actions";
import { EventCreationSteps } from "./ProposeEventModal";
import { ezlog } from "~/utils/ezlog";

export type ProposeEventFormData = RouterInputs["event"]["proposeEvent"] & {
    assignHost?: boolean;
    startTime24h?: string;
    endTime24h?: string;
    multiTagsSelected?: string[];
};

export const useCreateEvent = ({
    isOpen,
    mode,
    communitySlug,
    questionnaireCompletionId,
    isExternalEvent,
    setSteps,
}: {
    isOpen: boolean;
    mode: "create" | "propose";
    communitySlug: string;
    questionnaireCompletionId?: string;
    isExternalEvent?: boolean;
    setSteps: (steps: EventCreationSteps[]) => void;
}) => {
    const router = useRouter();
    const session = useSession();
    const form = useForm<ProposeEventFormData>();
    const {
        register,
        handleSubmit,
        getValues,
        watch,
        setValue,
        formState,
        control,
    } = form;

    const { data: user } = api.user.getUserById.useQuery(
        { userId: session?.data?.user.id ?? "" },
        {
            enabled: !!session?.data?.user.id,
        },
    );

    const { data: community } = api.community.getBySlug.useQuery({
        slug: communitySlug,
    });
    const [communityRequiredData, setCommunityRequiredData] = useState({
        ...community?.hostRequiredData,
        minBioLength: community?.requireHostProfileInformation ? 30 : 0,
    });

    const [selectedTemplate, setSelectedTemplate] = useState<
        EventTemplate | undefined
    >(undefined);

    const roundedMinutes = Math.ceil(DateTime.now().minute / 15) * 15;
    const currentDayPlus15 = DateTime.now()
        .plus({ days: 15 })
        .set({ minute: roundedMinutes })
        .setZone("UTC", { keepLocalTime: true })
        .toJSDate();
    const currentDayPlus15Plus2Hours = DateTime.now()
        .plus({ days: 15, hours: 2 })
        .set({ minute: roundedMinutes })
        .setZone("UTC", { keepLocalTime: true })
        .toJSDate();

    const createEvent = api.event.proposeEvent.useMutation();

    const [missingData, setMissingData] = useState<MissingUserData[]>([]);
    useEffect(() => {
        if (user && community) {
            const missingData = getMissingProfileData(community, user);
            setMissingData(missingData);
            setCommunityRequiredData({
                ...community?.hostRequiredData,
                minBioLength: community.requireHostProfileInformation ? 30 : 0,
            });
        }
    }, [user, community]);
    const refetchMissingData = () => {
        if (user && community) {
            getMissingProfileData(community, user);
        }
    };

    const handleNameChange = (e: SyntheticEvent) => {
        // react to name changes only when creating a new event
        const target = e.target as HTMLInputElement;
        const nameSlug = urlSafeSlugify(target.value);
        const entropySuffix = (Math.random() + 1).toString(36).substring(9);
        setValue("slug", `${nameSlug}-${entropySuffix}`, {
            shouldDirty: true,
        });
        setValue("name", target.value, { shouldDirty: true });
    };

    const onSubmit = async (data: ProposeEventFormData) => {
        if (!community) {
            return;
        }

        if (!session.data?.user?.id) {
            toast.error("You must be logged in to create an event.");
            return;
        }

        if (
            (isExternalEvent || form.watch("isExternalEvent")) &&
            !data.externalEventUrl
        ) {
            toast.error("No valid external URL provided");
            return;
        }

        toast.loading("Creating new Event", {
            toastId: "create-event-loading",
        });

        let startTime: Date;
        let endTime: Date | undefined;

        if (form.watch("isExternalEvent")) {
            if (form.watch("externalEventUrl")?.includes("lu.ma")) {
                startTime = data.startTime!;
                endTime = data.endTime!;
            } else {
                const startDate = data.startTime || new Date();
                startTime = mergeToTime(data.startTime24h!, startDate);
                if (data.endTime24h) {
                    endTime = mergeToTime(
                        data.endTime24h,
                        data.startTime || new Date(),
                    );
                }
            }
        } else if (data.startTime24h && data.startTime) {
            startTime = mergeToTime(data.startTime24h, data.startTime);
        } else {
            startTime = currentDayPlus15;
            endTime = currentDayPlus15Plus2Hours;
        }

        let eventSlug: string | undefined;
        const tags = [
            ...(data?.tags ?? []),
            ...(data?.multiTagsSelected ?? []),
        ];
        let resourceTags: string[] = [];

        if (!community.defaultTags.length) {
            const dateSuffix = new Date()
                .toLocaleString("en-US", { month: "short", year: "numeric" })
                .toLowerCase()
                .split(" ")
                .join("-");
            tags.push(`${communitySlug}-${dateSuffix}`);
            resourceTags.push(`${communitySlug}-${dateSuffix}`);
        }

        await createEvent.mutateAsync(
            {
                ...data,
                tags,
                resourceTags,
                startTime,
                endTime,
                timezone: !!data.timezone
                    ? data.timezone
                    : Intl.DateTimeFormat().resolvedOptions().timeZone,
                communityId: community.id,
                questionnaireCompletionId,
                mode,
                isExternalEvent:
                    isExternalEvent ?? form.watch("isExternalEvent"),
            },
            {
                onSuccess: async (res) => {
                    if (res) {
                        toast.dismiss("create-event-loading");
                        toast.success("Event created successfully.", {
                            autoClose: 1200,
                        });
                        eventSlug = res.slug;
                    }
                },
                onError: (e) => {
                    toast.dismiss("create-event-loading");
                    console.error(e);
                },
                onSettled: () => {
                    toast.dismiss("create-event-loading");
                    if (eventSlug) {
                        const panelMode: PanelStateTypes = "edit";
                        router.push(
                            `/events/${eventSlug}?panelMode=${panelMode}`,
                        );
                    }
                },
            },
        );
    };

    const submitTemplateMutation = api.event.clone.useMutation();
    const onSubmitTemplate = async () => {
        if (!selectedTemplate) {
            return;
        }
        if (!selectedTemplate.community?.slug) {
            toast.error("Unable to clone community-less event");
            return;
        }
        toast.loading("Cloning Event", {
            toastId: "clone-event-loading",
        });

        const isInPast = hasEndedUX(selectedTemplate);

        await submitTemplateMutation.mutateAsync(
            {
                communitySlug: selectedTemplate.community.slug,
                description: selectedTemplate.description ?? undefined,
                eventId: selectedTemplate.id,
                isSimulcast: false,
                isTemplate: true,
                isMicroAutoApproved: selectedTemplate.isMicroAutoApproved,
                currentUserIsHost: true,
                tags: [],
                startTime: isInPast
                    ? currentDayPlus15.toISOString()
                    : undefined,
                endTime: isInPast
                    ? currentDayPlus15Plus2Hours.toISOString()
                    : undefined,
            },
            {
                onSuccess: async (res) => {
                    if (res) {
                        toast.dismiss("clone-event-loading");
                        router.push(`/events/${res[0]?.slug}`);
                        toast.success("Event created successfully.", {
                            autoClose: 1200,
                        });
                    }
                },
                onError: (e) => {
                    toast.dismiss("clone-event-loading");
                    console.error(e);
                },
            },
        );
    };

    const handleChapterLeadTemplate = async () => {
        const template = await getTemplateForChapterLead({
            communitySlug,
        });

        if (template && isOpen) {
            setSelectedTemplate(template);
            setSteps(["chapter-lead-auto-submit"]);

            if (!template.community?.slug) {
                toast.error("Unable to clone community-less event");
                return;
            }

            toast.loading("Creating your chapter event", {
                toastId: "clone-event-loading",
            });

            const isInPast = hasEndedUX(template);
            await submitTemplateMutation.mutateAsync(
                {
                    communitySlug: template.community.slug,
                    description: template.description ?? undefined,
                    eventId: template.id,
                    isSimulcast: false,
                    isTemplate: true,
                    isMicroAutoApproved: template.isMicroAutoApproved,
                    currentUserIsHost: true,
                    tags: [],
                    startTime: isInPast
                        ? currentDayPlus15.toISOString()
                        : undefined,
                    endTime: isInPast
                        ? currentDayPlus15Plus2Hours.toISOString()
                        : undefined,
                },
                {
                    onSuccess: async (res) => {
                        if (res) {
                            toast.dismiss("clone-event-loading");
                            router.push(`/events/${res[0]?.slug}`);
                            toast.success("Event created successfully.", {
                                autoClose: 1200,
                            });
                        }
                    },
                    onError: (e) => {
                        toast.dismiss("clone-event-loading");
                        console.error(e);
                    },
                },
            );
        }
    };

    return {
        currentDayPlus15,
        currentDayPlus15Plus2Hours,
        createEvent,
        register,
        getValues,
        setValue,
        formState,
        communityRequiredData,
        missingData,
        refetchMissingData,
        handleNameChange,
        handleSubmit,
        onSubmit,
        onSubmitTemplate,
        selectedTemplate,
        setSelectedTemplate,
        watch,
        control,
        form,
        handleChapterLeadTemplate,
    };
};
